import React, { useEffect, useState } from "react";
import { Plus, Calendar as CalIcon, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";

const empty = { title: "", starts_at: "", ends_at: "", location: "", description: "" };

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/appointments"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/appointments", { ...form, starts_at: new Date(form.starts_at).toISOString(), ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null });
      toast.success("Appuntamento creato"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const day = date ? new Date(date).toDateString() : "";
  const dayItems = items.filter((a) => new Date(a.starts_at).toDateString() === day).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Calendario</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestione appuntamenti e visite immobili.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-appt-button"><Plus className="w-4 h-4" /> Nuovo appuntamento</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo appuntamento</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="appt-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inizio *</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="appt-starts-input" /></div>
                <div><Label>Fine</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></div>
              </div>
              <div><Label>Luogo</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Descrizione</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={submit} disabled={!form.title || !form.starts_at} data-testid="appt-submit-button">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border/60 p-4 lg:col-span-1 flex justify-center">
          <Calendar mode="single" selected={date} onSelect={setDate} className="rounded-md" />
        </Card>
        <Card className="border-border/60 p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-medium mb-4 flex items-center gap-2"><CalIcon className="w-4 h-4" />{date ? date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }) : ""}</h3>
          <div className="space-y-3">
            {dayItems.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Nessun appuntamento in questa data.</p>}
            {dayItems.map((a) => (
              <div key={a.id} className="border border-border/60 rounded-md p-4 hover:bg-muted/40 transition-colors" data-testid={`appt-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 text-center pt-1"><div className="text-xs text-muted-foreground uppercase">{new Date(a.starts_at).toLocaleDateString("it-IT", { month: "short" })}</div><div className="font-heading font-semibold text-lg tabular-nums">{new Date(a.starts_at).getDate()}</div></div>
                  <div className="flex-1">
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                      <span className="inline-flex items-center gap-1 tabular-nums"><Clock className="w-3 h-3" />{new Date(a.starts_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                      {a.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.location}</span>}
                    </div>
                    {a.description && <p className="text-sm text-muted-foreground mt-2">{a.description}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
